import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos, categories } from '@/mocks/videos';

const categoryHeroImages: Record<string, string> = {
  remen: "https://readdy.ai/api/search-image?query=Warm%20cinematic%20background%20with%20soft%20amber%20gradient%20and%20subtle%20geometric%20patterns%2C%20clean%20minimalist%20design%2C%20elegant%20atmosphere%20with%20golden%20highlights&width=1400&height=400&seq=cat-hero-remen&orientation=landscape",
  dianying: "https://readdy.ai/api/search-image?query=Warm%20cinematic%20film%20atmosphere%20with%20soft%20amber%20gradient%2C%20movie%20themed%20abstract%20background%2C%20clean%20minimalist%20design%2C%20elegant%20composition&width=1400&height=400&seq=cat-hero-dianying&orientation=landscape",
  dianshiju: "https://readdy.ai/api/search-image?query=Warm%20television%20drama%20atmosphere%20with%20soft%20golden%20lighting%2C%20abstract%20geometric%20patterns%2C%20clean%20minimalist%20design%2C%20elegant%20composition&width=1400&height=400&seq=cat-hero-dianshiju&orientation=landscape",
  zongyi: "https://readdy.ai/api/search-image?query=Warm%20entertainment%20variety%20show%20atmosphere%20with%20soft%20amber%20gradient%2C%20playful%20abstract%20shapes%2C%20clean%20minimalist%20design%2C%20energetic%20composition&width=1400&height=400&seq=cat-hero-zongyi&orientation=landscape",
  dongman: "https://readdy.ai/api/search-image?query=Warm%20animation%20themed%20background%20with%20soft%20orange%20gradient%2C%20abstract%20artistic%20patterns%2C%20clean%20minimalist%20design%2C%20creative%20composition&width=1400&height=400&seq=cat-hero-dongman&orientation=landscape",
  jilupian: "https://readdy.ai/api/search-image?query=Warm%20documentary%20themed%20background%20with%20soft%20golden%20gradient%2C%20nature%20inspired%20abstract%20patterns%2C%20clean%20minimalist%20design%2C%20elegant%20composition&width=1400&height=400&seq=cat-hero-jilupian&orientation=landscape",
  shaoer: "https://readdy.ai/api/search-image?query=Warm%20children%20themed%20background%20with%20soft%20amber%20gradient%2C%20playful%20abstract%20shapes%2C%20clean%20minimalist%20design%2C%20cheerful%20composition&width=1400&height=400&seq=cat-hero-shaoer&orientation=landscape",
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'hot' | 'new' | 'rating'>('hot');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryVideos = videos.filter((v) => v.category === currentCategory.slug);

  const sortedVideos = [...categoryVideos].sort((a, b) => {
    if (activeSort === 'hot') return b.views - a.views;
    if (activeSort === 'rating') return b.rating - a.rating;
    return 0;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const heroImage = categoryHeroImages[currentCategory.slug] || categoryHeroImages.remen;

  const pageTitle = `${currentCategory.name} - 爱液视频${currentCategory.name}在线观看`;
  const pageDescription = `${currentCategory.description}，爱液视频${currentCategory.name}频道为您精选海量正版高清${currentCategory.name}内容。`;
  const pageKeywords = `爱液视频${currentCategory.name},${currentCategory.name}在线观看,${currentCategory.name}高清播放,爱液视频`;

  return (
    <>
      <ScrollToTop />
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl={`https://www.aiyevideo.com/category/${currentCategory.slug}`}
      />

      <header>
        <Navbar />
      </header>

      <main className="pt-16">
        {/* Category Hero */}
        <section className="relative h-48 md:h-64 flex items-center justify-center overflow-hidden">
          <img
            src={heroImage}
            alt={`${currentCategory.name} - 爱液视频`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
          <div className="relative z-10 text-center px-4">
            <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-primary-500/90 text-background-50">
              <i className={`${currentCategory.icon} text-2xl`}></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">{currentCategory.name}</h1>
            <p className="text-sm text-background-50/80 mt-2 max-w-md mx-auto">{currentCategory.description}</p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        {/* Category Tabs + Sort */}
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      currentCategory.slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={cat.icon}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5">
                {[
                  { key: 'hot' as const, label: '最热', icon: 'ri-fire-line' },
                  { key: 'new' as const, label: '最新', icon: 'ri-time-line' },
                  { key: 'rating' as const, label: '高分', icon: 'ri-star-line' },
                ].map((sort) => (
                  <button
                    key={sort.key}
                    onClick={() => setActiveSort(sort.key)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeSort === sort.key
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={sort.icon}></i>
                    {sort.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Video Grid */}
        <section className="bg-background-50 py-8 md:py-10">
          <div className="w-full px-4 md:px-6">
            {sortedVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-product-shop="">
                {sortedVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无视频内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}